import { onChildRemoved, remove, Unsubscribe } from "firebase/database";
import {
    createContext,
    Dispatch,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import { AbrirAtendimentoSelecionandoTipoCaso } from "../../Components/CardsPages/Atendimento/AbrirAtendimento";
import { IComponentModal } from "../../Components/Modais/IModalComponent";
import { ListarProdutosPorContaCompleto } from "../../Controllers/Cobranca/ContaProduto/ContaProdutoController";
import {
    ListarAtividadesNotificacaoUsuario,
    ListarNotificacoesTransferenciaController,
    ListarUltimasNotificacoesUsuario,
    ListenNotificacoesAlertaController,
    ListenNotificacoesURAController,
} from "../../Controllers/Geral/Notificacao/NotificacaoControllers";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import { useTransformToRef } from "../../hooks/useTransformToRef";
import { IClienteCompleto } from "../../Interfaces/Geral/Cliente/IClienteSimples";
import {
    IAtividadeNotificacao,
    IUltimasNotificacoes,
    IUltimasTransferencias,
} from "../../Interfaces/Geral/Notificacao/NotificacaoInterfaces";
import { ACESSOS } from "../../Routes/Acessos";
import { Notificate } from "../../Tools/Notification";
import { AlertConfirmation } from "../../Tools/SweetAlerts";
import { useClientePage } from "../ClientePage";
import { useUserInfor } from "../User";
import notificationSound from "../../Assets/sounds/notification.mp3";

interface INotificacaoAlertaProps {
    updateNotificacoes: (
        which?: "notificacoes" | "atividades" | "transferencias"
    ) => Promise<void>;
    ultimasNotificacoes: IUltimasNotificacoes | undefined;
    ultimasTransferencias: IUltimasTransferencias | undefined;
    lstAtividadesAtendimentos: Array<IAtividadeNotificacao> | undefined;
    show: boolean;
    setShow: Dispatch<SetStateAction<boolean>>;
    modal: IComponentModal | undefined;
    setModal: Dispatch<SetStateAction<IComponentModal | undefined>>;
    Cliente: IClienteCompleto | undefined;
    modulo: "Geral" | "Cobranca" | "Atendimento";
}
interface INotificacaoAlertaProviderProps {
    children: React.ReactNode;
}

const objTipoCaso: Record<
    number,
    { link: (IDCaso: number) => string } | undefined
> = {
    [ACESSOS.COBRANCA.ID_MODULO]: {
        link: ACESSOS.COBRANCA.TELAS.ATENDIMENTO.DS_LINK,
    },
    [ACESSOS.ATENDIMENTO.ID_MODULO]: {
        link: ACESSOS.ATENDIMENTO.TELAS.ATENDIMENTO.DS_LINK,
    },
    [ACESSOS.ASSISTENCIA.ID_MODULO]: {
        link: ACESSOS.ASSISTENCIA.TELAS.ATENDIMENTO.DS_LINK,
    },
    [ACESSOS.VENDAS.ID_MODULO]: {
        link: ACESSOS.VENDAS.TELAS.ATENDIMENTO.DS_LINK,
    },
    [ACESSOS.REPASSE.ID_MODULO]: {
        link: ACESSOS.REPASSE.TELAS.ATENDIMENTO.DS_LINK,
    },
    [ACESSOS.ENTREGA_DE_CHAVES.ID_MODULO]: {
        link: ACESSOS.ENTREGA_DE_CHAVES.TELAS.ATENDIMENTO.DS_LINK,
    },
};

const NotificacaoAlertaContext = createContext({
    updateNotificacoes: () => {},
} as INotificacaoAlertaProps);

export function NotificacaoAlertaProvider(
    props: INotificacaoAlertaProviderProps
) {
    const history = useHistory();

    const { Cliente, modulo } = useClientePage();

    const [show, setShow] = useState(false);
    const [modal, setModal] = useState<IComponentModal>();
    const {
        UserInfor: { ID_USUARIO, MODULOS },
        OrgInfor: { ID_ORGANIZACAO },
    } = useUserInfor();

    const { value: ultimasNotificacoes, execute: updateUltimasNotificacoes } =
        useAsyncEffect(async () => {
            if (MODULOS) return ListarUltimasNotificacoesUsuario();
        }, [MODULOS]);

    const {
        value: ultimasTransferencias,
        execute: updateUltimasTransferencias,
    } = useAsyncEffect(async () => {
        if (MODULOS) return ListarNotificacoesTransferenciaController();
    }, [MODULOS]);

    const {
        value: lstAtividadesAtendimentosLoad,
        execute: updateAtividadesAtendimentos,
    } = useAsyncEffect(async () => {
        if (MODULOS) {
            return ListarAtividadesNotificacaoUsuario().then((res) =>
                res.filter((atv) =>
                    MODULOS?.some((m) => m.ID_MODULO === atv.ID_TIPO_CASO)
                )
            );
        }
    }, [MODULOS]);
    const lstAtividadesAtendimentos = useMemo(() => {
        return lstAtividadesAtendimentosLoad?.filter((atv) =>
            MODULOS?.some((m) => m.ID_MODULO === atv.ID_TIPO_CASO)
        );
    }, [lstAtividadesAtendimentosLoad, MODULOS]);

    const updateNotificacoes = useCallback(
        async (which?: "notificacoes" | "atividades" | "transferencias") => {
            if (which !== "atividades") await updateUltimasNotificacoes();
            if (which !== "notificacoes") await updateAtividadesAtendimentos();
            if (which !== "transferencias") await updateUltimasTransferencias();
        },
        [
            updateUltimasNotificacoes,
            updateAtividadesAtendimentos,
            updateUltimasTransferencias,
        ]
    );

    const updateNotificacoesRef = useTransformToRef(updateNotificacoes);

    const audio = new Audio(notificationSound);

    useEffect(() => {
        let unsubscribe: Unsubscribe;

        const playSound = () => {
            audio.play();
        };

        if (ID_USUARIO) {
            unsubscribe = ListenNotificacoesAlertaController(
                ID_ORGANIZACAO,
                ID_USUARIO,
                (notificacaoAlerta, snap) => {
                    if (!notificacaoAlerta.FL_ALERTA_NOTIFICACAO) {
                        playSound();
                        Notificate(
                            notificacaoAlerta.DS_TITULO,
                            notificacaoAlerta.DS_MENSAGEM
                        );
                        remove(snap.ref);
                    } else {
                        playSound();
                        const link =
                            objTipoCaso[notificacaoAlerta.ID_TIPO_CASO]?.link;
                        AlertConfirmation(
                            notificacaoAlerta.DS_TITULO,
                            notificacaoAlerta.DS_MENSAGEM,
                            "info",
                            {
                                confirmButtonText: link ? "Visualizar" : false,
                                cancelButtonText: "Fechar",
                            }
                        ).then((result) => {
                            if (result) {
                                remove(snap.ref);
                                if (result.isConfirmed && link)
                                    history.push(
                                        link(notificacaoAlerta.ID_CASO)
                                    );
                            }
                        });

                        onChildRemoved(snap.ref, () => {
                            Swal.close();
                        });
                    }

                    updateNotificacoesRef.current();
                }
            );
        }

        return function cleanup() {
            unsubscribe?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ID_USUARIO, ID_ORGANIZACAO, updateNotificacoesRef, history]);

    useEffect(() => {
        let unsubscribe: Unsubscribe;

        const playSound = () => {
            audio.play();
        };
        if (ID_USUARIO) {
            unsubscribe = ListenNotificacoesURAController(
                ID_ORGANIZACAO,
                ID_USUARIO,
                (notificacaoUra, snap) => {
                    if (notificacaoUra) {
                        if (notificacaoUra.DS_STATUS === "URA_SUCCESS") {
                            playSound();
                            const link =
                                objTipoCaso[notificacaoUra.ID_TIPO_CASO]?.link;
                            AlertConfirmation(
                                notificacaoUra?.DS_TITULO,
                                notificacaoUra?.DS_MENSAGEM,
                                "success",
                                {
                                    confirmButtonText:
                                        notificacaoUra.ID_CLIENTE &&
                                        notificacaoUra.ID_CLIENTE !== 0
                                            ? "Ver Cliente"
                                            : "Ver Atendimento",
                                    cancelButtonText: "Fechar",
                                }
                            ).then(async (result) => {
                                if (result) {
                                    remove(snap.ref);
                                    if (
                                        result.isConfirmed &&
                                        notificacaoUra.ID_CLIENTE &&
                                        notificacaoUra.ID_CLIENTE !== 0
                                    ) {
                                        history.push(
                                            `/Atendimento/Cliente/${notificacaoUra.ID_CLIENTE}`
                                        );
                                        setShow(true);
                                        ListarProdutosPorContaCompleto(
                                            notificacaoUra.ID_CLIENTE,
                                            2
                                        ).then((res) => {
                                            AbrirAtendimentoSelecionandoTipoCaso(
                                                setShow,
                                                setModal,
                                                {
                                                    ID_MODULO: 2,
                                                    editCliente: false,
                                                    defaultDesc: `Atendimento aberto pela tela de Cliente`,
                                                    setShow: setShow,
                                                    cliente: {
                                                        ...Cliente,
                                                        ID_CONTA:
                                                            notificacaoUra.ID_CLIENTE ||
                                                            0,
                                                        DS_CONTA:
                                                            Cliente?.DS_NOME ||
                                                            "",
                                                        PRODUTOS: res,
                                                    },
                                                }
                                            );
                                        });
                                    } else if (result.isConfirmed && link) {
                                        history.push(
                                            link(notificacaoUra?.ID_CASO)
                                        );
                                    }
                                }
                            });

                            onChildRemoved(snap.ref, () => {
                                Swal.close();
                            });
                        } else if (notificacaoUra.DS_STATUS === "URA_WARNING") {
                            playSound();
                            AlertConfirmation(
                                notificacaoUra.DS_TITULO,
                                notificacaoUra.DS_MENSAGEM,
                                "info",
                                {
                                    confirmButtonText: "Ver",
                                    cancelButtonText: "Fechar",
                                }
                            ).then((result) => {
                                if (result) {
                                    remove(snap.ref);
                                    if (result.isConfirmed)
                                        history.push(`/Geral/GestaoClientes/`);
                                }
                            });

                            onChildRemoved(snap.ref, () => {
                                Swal.close();
                            });
                        }
                    }
                }
            );
        }

        return function cleanup() {
            unsubscribe?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ID_USUARIO, ID_ORGANIZACAO, history, Cliente]);

    return (
        <NotificacaoAlertaContext.Provider
            value={{
                ultimasNotificacoes,
                lstAtividadesAtendimentos,
                ultimasTransferencias,
                updateNotificacoes,
                modal,
                setModal,
                show,
                setShow,
                Cliente,
                modulo,
            }}
        >
            {props.children}
        </NotificacaoAlertaContext.Provider>
    );
}

export function useNotificacaoAlerta() {
    return useContext(NotificacaoAlertaContext);
}
